import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { Search, MapPin, X } from "lucide-react";

export default function ArtistsDirectory() {
    const [params, setParams] = useSearchParams();
    const [artists, setArtists] = useState([]);
    const [meta, setMeta] = useState({ cities: [], countries: [], genres: [] });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState(params.get("q") || "");

    const filters = useMemo(
        () => ({
            q: params.get("q") || undefined,
            genre: params.get("genre") || undefined,
            city: params.get("city") || undefined,
            country: params.get("country") || undefined,
            local: params.get("local") || undefined,
        }),
        [params],
    );

    useEffect(() => {
        endpoints.meta().then(setMeta).catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        endpoints
            .listArtists(filters)
            .then((d) => {
                setArtists(d.artists);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [filters]);

    const setFilter = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value);
        else next.delete(key);
        setParams(next, { replace: true });
    };

    const clearAll = () => setParams({}, { replace: true });

    return (
        <div data-testid="artists-directory-page" className="pm-fade">
            <section className="border-b border-[#2a2a2a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-10 md:py-14">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">
                        Directory
                    </div>
                    <h1 className="mt-2 font-display font-black uppercase tracking-tighter text-4xl md:text-6xl">
                        Artists
                    </h1>
                    <p className="mt-4 text-[#a3a3a3] max-w-xl">
                        Browse local and international artists. Follow the ones you love. Their events show up here.
                    </p>
                </div>
            </section>

            {/* Filter bar */}
            <section className="border-b border-[#2a2a2a] sticky top-16 z-30 bg-[#0a0a0a]">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row gap-3 md:items-center">
                    <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1 min-w-0">
                        <Search className="w-4 h-4 text-[#666]" />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && setFilter("q", q)}
                            onBlur={() => setFilter("q", q)}
                            placeholder="Search by artist name…"
                            className="bg-transparent outline-none flex-1 text-sm placeholder:text-[#666]"
                            data-testid="artists-search-input"
                        />
                    </div>
                    <Select
                        label="Genre"
                        value={filters.genre || ""}
                        options={meta.genres}
                        onChange={(v) => setFilter("genre", v)}
                        testId="filter-genre"
                    />
                    <Select
                        label="City"
                        value={filters.city || ""}
                        options={meta.cities}
                        onChange={(v) => setFilter("city", v)}
                        testId="filter-city"
                    />
                    <Select
                        label="Country"
                        value={filters.country || ""}
                        options={meta.countries}
                        onChange={(v) => setFilter("country", v)}
                        testId="filter-country"
                    />
                    <Select
                        label="Scope"
                        value={filters.local || ""}
                        options={["local", "international"]}
                        onChange={(v) => setFilter("local", v)}
                        testId="filter-scope"
                    />
                    <button
                        onClick={clearAll}
                        className="pm-btn"
                        data-testid="artists-clear-filters"
                    >
                        <X className="w-3 h-3 mr-1" /> Clear
                    </button>
                </div>
            </section>

            <section>
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
                    <div className="text-xs font-mono uppercase tracking-widest text-[#666] mb-4" data-testid="artists-count">
                        {loading ? "Loading…" : `${artists.length} artists`}
                    </div>
                    {!loading && artists.length === 0 && (
                        <div className="border border-[#2a2a2a] p-10 text-center text-[#a3a3a3]" data-testid="artists-empty">
                            No artists match those filters.
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                        {artists.map((a) => (
                            <Link
                                key={a.id}
                                to={`/artists/${a.id}`}
                                data-testid={`directory-artist-${a.id}`}
                                className="group bg-[#0a0a0a] p-4 flex flex-col hover:bg-[#141414] transition-colors"
                            >
                                <div className="relative aspect-square overflow-hidden">
                                    <img
                                        src={a.image_url}
                                        alt=""
                                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                                    />
                                </div>
                                <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">
                                    {a.genre}
                                </div>
                                <div className="font-display font-black uppercase text-lg leading-tight mt-1">
                                    {a.name}
                                </div>
                                <div className="mt-1 text-xs text-[#a3a3a3] flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {a.city}, {a.country}
                                </div>
                                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#666]">
                                    {a.is_local ? "Local" : "International"} · {a.followers.toLocaleString()} fans
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Select({ label, value, options, onChange, testId }) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="appearance-none bg-transparent border border-[#2a2a2a] px-3 pr-8 py-2 text-sm font-mono uppercase tracking-wide text-[#f4f4f0] hover:border-[#f4f4f0] focus:border-[#ff4400] outline-none"
            >
                <option value="" className="bg-[#141414]">{label}: All</option>
                {options.map((o) => (
                    <option key={o} value={o} className="bg-[#141414]">
                        {o}
                    </option>
                ))}
            </select>
        </div>
    );
}
